import axios from "axios";
import store from "../../store";
import { logout } from "../../store/actions";
import { refreshAccessToken, clearRefreshTimer } from "../../auth/tokenRefresh";
import { getDeviceId, storeDeviceId } from "../deviceId";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// ---------------------------------------------------------------------------
// Local-identity auth (password / OTP / signup) - see auth/authApi.js.
// Separate instances so the sign-in endpoints never inherit the bearer header or the 401 ->
// logout interceptor below (a wrong password must show an error, not bounce the page).
// ---------------------------------------------------------------------------
const rejectWithResponse = (error) => Promise.reject(error?.response || error);

// Public, cookie-free endpoints (brand/manufacturer login, OTP request, forgot/reset password).
const publicClient = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });
publicClient.interceptors.response.use((response) => response, rejectWithResponse);

// Endpoints that set, send or clear the HttpOnly refresh cookie need withCredentials, plus the
// X-Device-Id header the backend requires as its CSRF defence on the cookie-authenticated calls.
const authClient = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL, withCredentials: true });
authClient.interceptors.response.use((response) => {
  // The server mints a device id when a sign-in arrives without one and echoes it back.
  storeDeviceId(response?.headers?.["x-device-id"]);
  return response;
}, rejectWithResponse);

export const axiosPostNoAuth = (url, payload) => publicClient.post(url, payload);
export const axiosGetNoAuth = (url, config) => publicClient.get(url, config);
export const axiosPostAuth = (url, payload, config = {}) =>
  authClient.post(url, payload, {
    ...config,
    headers: { "X-Device-Id": getDeviceId(), ...(config.headers || {}) },
  });

api.interceptors.request.use((config) => {
  const token = store.getState().auth?.accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function forceLogout() {
  clearRefreshTimer();
  store.dispatch(logout());
  if (window.location.pathname !== "/") {
    window.location.assign("/");
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    // Only SSO sessions carry a refresh token; a password-login JWT that 401s just logs out below.
    const hasRefreshToken = !!store.getState().auth?.refreshToken;

    if (status === 401 && originalRequest && !originalRequest._retry && hasRefreshToken) {
      originalRequest._retry = true;
      return refreshAccessToken()
        .then((newAccessToken) => {
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };
          return api(originalRequest);
        })
        .catch((refreshErr) => {
          forceLogout();
          return Promise.reject(refreshErr?.response || refreshErr);
        });
    }

    if (status === 401) {
      forceLogout();
    }
    return Promise.reject(error?.response || error);
  }
);

export const axiosGet = (url, config) => api.get(url, config);
export const axiosPost = (url, payload, config) => api.post(url, payload, config);
export const axiosPut = (url, payload, config) => api.put(url, payload, config);
export const axiosPatch = (url, payload, config) => api.patch(url, payload, config);
export const axiosDelete = (url, config) => api.delete(url, config);

export default api;
